import { Question } from '../services/surveyService';

/**
 * 问卷显示工具函数
 */

// 用户角色类型
export type UserRole = 'user' | 'approver' | 'admin';

// 问卷填写阶段
export type SurveyStage = 'filling' | 'approval' | 'view';

/**
 * 根据用户角色和阶段过滤问题
 * @param questions 所有问题
 * @param userRole 用户角色
 * @param stage 当前阶段
 * @returns 过滤后的问题列表
 */
export const filterQuestionsByRole = (
  questions: Question[],
  userRole: UserRole,
  stage: SurveyStage
): Question[] => {
  return questions.filter(question => {
    // 管理员可以看到所有问题
    if (userRole === 'admin') {
      return true;
    }

    // 用户填写阶段 - 只显示用户需要回答的问题
    if (stage === 'filling' && userRole === 'user') {
      return question.answered_by === 'user';
    }

    // 审批阶段 - 显示所有问题，但区分回答权限
    if (stage === 'approval' && userRole === 'approver') {
      return true;
    }

    // 查看阶段 - 显示所有问题
    if (stage === 'view') {
      return true;
    }

    return false;
  });
};

/**
 * 检查问题是否可编辑/回答
 * @param question 问题对象
 * @param userRole 用户角色
 * @param stage 当前阶段
 * @returns 是否可编辑
 */
export const canAnswerQuestion = (
  question: Question,
  userRole: UserRole,
  stage: SurveyStage
): boolean => {
  // 管理员始终可编辑
  if (userRole === 'admin') {
    return true;
  }

  // 用户填写阶段 - 只能回答用户问题
  if (stage === 'filling' && userRole === 'user') {
    return question.answered_by === 'user';
  }

  // 审批阶段 - 审批人只能回答审批人问题
  if (stage === 'approval' && userRole === 'approver') {
    return question.answered_by === 'approver';
  }

  return false;
};

/**
 * 获取问题的回答人标签
 * @param question 问题对象
 * @returns 回答人标签信息
 */
export const getAnsweredByLabel = (question: Question) => {
  const labelMap = {
    'user': { text: '用户回答', color: '#1890ff' },
    'approver': { text: '审批人回答', color: '#52c41a' }
  };
  
  return labelMap[question.answered_by] || { text: '未知', color: '#999' };
};

/**
 * 分离用户问题和审批人问题
 * @param questions 所有问题
 * @returns 分离后的问题
 */
export const separateQuestions = (questions: Question[]) => {
  const userQuestions = questions.filter(q => q.answered_by === 'user');
  const approverQuestions = questions.filter(q => q.answered_by === 'approver');
  
  return {
    userQuestions: userQuestions.sort((a, b) => (a.order_num || 0) - (b.order_num || 0)),
    approverQuestions: approverQuestions.sort((a, b) => (a.order_num || 0) - (b.order_num || 0))
  };
};

/**
 * 获取问题的状态提示文本
 * @param question 问题对象
 * @param userRole 用户角色
 * @param stage 当前阶段
 * @returns 状态提示文本
 */
export const getQuestionStatusHint = (
  question: Question,
  userRole: UserRole,
  stage: SurveyStage
): string => {
  if (canAnswerQuestion(question, userRole, stage)) {
    return '';
  }

  if (stage === 'filling' && userRole === 'user' && question.answered_by === 'approver') {
    return '此问题将由审批人填写';
  }

  if (stage === 'approval' && userRole === 'approver' && question.answered_by === 'user') {
    return '此问题由用户填写，仅供查看';
  }

  return '您无权回答此问题';
}; 